/* eslint-disable complexity */
import { flatten } from 'lodash-es';
import { isUUID } from 'class-validator';
import { format, isMatch, parse, isValid } from 'date-fns';
import { IntakeFormSection, IntakeFormSectionField, IntakeFormUserDataResults, IntakeFormValue } from '../models/dto';

export const intakeFormDateFormat = 'd/M/yyyy';

export type IntakeFormColumns = {
	id: string;
	name: string;
	field: IntakeFormSectionField;
};

// ids match or names match
const intakeOptionFilter = (
	o: { id: string; name: string },
	value: string[] | Array<Record<string, boolean>>
): boolean => value.some(v => {
	if (typeof v === 'string') {
		return v.includes(o.name) || v.includes(o.id);
	}

	const obj = (v || {}) as Record<string, boolean>;

	return !!Object.keys(obj).find(k => k === o.id || k === o.name);
});

export const getIntakeFormSectionFieldRawValueFromResults = (
	field: IntakeFormSectionField,
	results?: IntakeFormUserDataResults
): IntakeFormValue | undefined => {
	if (!results) {
		return undefined;
	}

	const value = (results as Record<string, boolean | string | string[]>)[field.name];

	if (!value) {
		return undefined;
	}

	if ((field.type === 'drop-down' || field.type === 'radio-buttons') && typeof value === 'string') {
		let option = (field.options ?? []).find(o => o.id === value || o.name === value);

		if (!option) {
			option = (field.options ?? []).find(o => o.name.includes(value));
		}

		return option?.id;
	}

	if (field.type === 'checkbox-list' && Array.isArray(value)) {
		const options = (field.options ?? []).filter(o => intakeOptionFilter(o, value));

		return options.map(o => o.id);
	}

	if (field.type === 'date-picker' && typeof value === 'string') {
		if (isMatch(value, intakeFormDateFormat)) {
			return parse(value, intakeFormDateFormat, new Date());
		}

		return isValid(new Date(value)) ? new Date(value) : undefined;
	}

	return value;
};

export const getIntakeFormSectionFieldValueFromResults = (
	field: IntakeFormSectionField,
	results?: IntakeFormUserDataResults,
	{ resultDateFormat = 'MM/dd/yyyy' }: { resultDateFormat?: string } = {}
): string => {
	const value = getIntakeFormSectionFieldRawValueFromResults(field, results);

	if (!value) {
		return '';
	}

	if ((field.type === 'drop-down' || field.type === 'radio-buttons') && typeof value === 'string') {
		let option = (field.options ?? []).find(o => o.id === value || o.name === value);

		if (!option) {
			option = (field.options ?? []).find(o => o.name.includes(value));
		}

		return option?.name ?? (isUUID(value) ? '' : value);
	}

	if (field.type === 'checkbox-list' && Array.isArray(value)) {
		const options = (field.options ?? []).filter(o => intakeOptionFilter(o, value));
		const optionsIds = options.map(o => o.id);

		const optionsNames = [...options.map(o => o.name), ...value.filter(v => !isUUID(v) && !optionsIds.includes(v))];

		return optionsNames.join(', ');
	}

	if (field.type === 'date-picker' && (value instanceof Date || typeof value === 'string')) {
		const date = new Date(value);

		return isValid(date) ? format(date, resultDateFormat) : '';
	}

	return Array.isArray(value) ? value.join(', ') : `${value}`;
};

export const convertIntakeFormSectionsToColumns = (sections: IntakeFormSection[]): IntakeFormColumns[] =>
	flatten(
		sections.map(section => {
			const { sectionName, fields = [] } = section;

			return fields.map(field => ({
				id: field.name as string,
				name: `${sectionName ? `${sectionName} - ` : ''}${field.label ?? field.checkboxLabel}`,
				field,
			}));
		})
	);

export const convertIntakeFormSectionsToGettersMap = (
	sections: IntakeFormSection[]
): Record<string, (intakeFormResults?: IntakeFormUserDataResults) => string> => {
	const gettersMap = sections.reduce((acc, section) => {
		const { sectionName, fields = [] } = section as IntakeFormSection;

		return {
			...acc,
			...fields.reduce(
				(acc2, field) => ({
					...acc2,
					[`${sectionName ? `${sectionName} - ` : ''}${field.label ?? field.checkboxLabel}`]: (
						intakeFormResults?: IntakeFormUserDataResults
					) => {
						if (!intakeFormResults) {
							return;
						}

						return getIntakeFormSectionFieldValueFromResults(field, intakeFormResults);
					},
				}),
				{}
			),
		};
	}, {});

	return gettersMap as Record<string, (intakeFormResults?: IntakeFormUserDataResults) => string>;
};
